/**
 * AIDraftWarningBanner — the MANDATORY warning shown every time AI
 * pre-populates form data (locked product requirement).
 *
 * Behavior rules (do not weaken):
 * - Rendered by ALL application paths: AI Prefill panel, chat→form handoff,
 *   and "start from this" on a similar record.
 * - Persistent and sticky — visible on every wizard step, cannot scroll away.
 * - Collapsible to a slim strip but NEVER fully dismissible while any
 *   AI-filled field remains un-reviewed; re-expands on every new application.
 * - No "don't show again"; no session suppression.
 * - Lists low-certainty fields by name.
 *
 * The identical warning copy (AI_WARNING_TEXT) is also printed by the chat
 * widget next to its "Open pre-filled form" action.
 */

import React, { useEffect, useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';

export const AI_WARNING_TEXT =
  'This form contains AI-generated content. AI can make mistakes — review and verify every pre-filled field before submitting.';

export default function AIDraftWarningBanner({
  aiFieldCount,          // number of AI-filled fields not yet edited by the user
  lowCertaintyFields = [], // [{ path, label }] fields the AI is least sure about
  appliedAt,             // timestamp of the latest application — re-expands banner
}) {
  const [collapsed, setCollapsed] = useState(false);

  // Every new prefill application re-expands the banner in full.
  useEffect(() => {
    if (appliedAt) setCollapsed(false);
  }, [appliedAt]);

  if (!aiFieldCount || aiFieldCount <= 0) return null;

  if (collapsed) {
    return (
      <div
        className="sticky top-0 z-40 flex items-center justify-between gap-2 rounded-md border border-amber-400 bg-amber-50 px-3 py-1.5 text-sm text-amber-900 shadow-sm"
        role="alert"
      >
        <span className="flex items-center gap-2 font-medium">
          <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
          AI-generated content — {aiFieldCount} field{aiFieldCount === 1 ? '' : 's'} still to verify
        </span>
        <button
          type="button"
          onClick={() => setCollapsed(false)}
          className="flex items-center gap-1 text-amber-800 underline hover:text-amber-950"
        >
          Details <ChevronDown className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div
      className="sticky top-0 z-40 rounded-md border-2 border-amber-400 bg-amber-50 px-4 py-3 text-amber-900 shadow-md"
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
          <div>
            <p className="font-semibold">{AI_WARNING_TEXT}</p>
            <p className="mt-1 text-sm">
              {aiFieldCount} AI-filled field{aiFieldCount === 1 ? '' : 's'} not yet
              reviewed — editing a field marks it as verified.
            </p>
            {lowCertaintyFields.length > 0 && (
              <p className="mt-1 text-sm">
                <span className="font-medium">Check these especially carefully: </span>
                {lowCertaintyFields.map((f) => f.label).join(', ')}
              </p>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={() => setCollapsed(true)}
          className="flex shrink-0 items-center gap-1 text-sm text-amber-800 underline hover:text-amber-950"
          title="Collapse (the warning stays visible while AI-filled fields remain)"
        >
          Collapse <ChevronUp className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

/** Slim reminder for the submit-button row while unverified AI fields exist. */
export function AISubmitReminder({ aiFieldCount }) {
  if (!aiFieldCount || aiFieldCount <= 0) return null;
  return (
    <span className="flex items-center gap-1 text-sm font-medium text-amber-700">
      <AlertTriangle className="h-4 w-4" />
      {aiFieldCount} AI-filled field{aiFieldCount === 1 ? '' : 's'} — verified?
    </span>
  );
}

/**
 * Mandatory pre-submit acknowledgement whenever AI populated any field this
 * session. The page must disable its submit action until `checked` is true,
 * and reset `checked` to false on every new draft application.
 */
export function AIVerifyAcknowledgement({ checked, onChange, unreviewedCount = 0 }) {
  return (
    <label
      className="flex cursor-pointer items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm text-amber-900"
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 shrink-0 accent-amber-600"
      />
      <span>
        <span className="font-semibold">
          I have reviewed and verified all AI-generated fields on this form.
        </span>
        {unreviewedCount > 0 && (
          <span className="mt-0.5 block text-xs">
            {unreviewedCount} AI-filled field{unreviewedCount === 1 ? '' : 's'} not
            edited since generation — double-check {unreviewedCount === 1 ? 'it' : 'them'} before ticking.
          </span>
        )}
      </span>
    </label>
  );
}
